import Dexie from 'dexie';
import { applyEncryptionMiddleware } from 'lib/dexie';

// The browser copies autosave keeps (controllers/auto_save_controller.js), one row per copy.
export const FORM_STORE_NAME = 'FormStore';

// How a sign-out reaches autosave in the person's other tabs (`suspendAutoSave`).
export const FORM_STORE_CHANNEL = 'form-store';
const SUSPEND_EVENT = 'auto-save:suspend';

// `dexieOptions` is for specs, which hand in fake-indexeddb.
export function openFormStore(dexieOptions = {}) {
  const db = new Dexie(FORM_STORE_NAME, dexieOptions);
  db.version(2).stores({
    forms: `
      ++id,
      form_id,
      time,
      submitted,
      [form_id+submitted]
    `,
  });

  applyEncryptionMiddleware(db);
  return db;
}

// Deletes the copies one person wrote of the given forms (`formIds`), or of every form whose id starts with
// `formIdPrefix`. With neither it deletes nothing: one person can autosave both as staff and as a parent, and a
// staff copy is the safety net for a Save that did not land. Another person's copies on the same browser are left
// alone. Resolves with the number of copies deleted.
//
// `form_id` is an index and stored in the clear, so it narrows the rows. `user_id` is stored encrypted, so the rows
// are read through the middleware and compared after decryption, then deleted by id. A filtered
// `Collection#delete()` deletes nothing in Chrome here (it does under fake-indexeddb).
export async function deleteCopies(db, { userId, formIds = null, formIdPrefix = null }) {
  if (!userId) return 0;

  let copies;
  if (formIds?.length > 0) {
    copies = db.forms.where('form_id').anyOf(formIds);
  } else if (formIdPrefix) {
    copies = db.forms.where('form_id').startsWith(formIdPrefix);
  } else {
    return 0;
  }
  const ids = (await copies.toArray()).filter((copy) => copy.user_id === userId).map((copy) => copy.id);
  await db.forms.bulkDelete(ids);
  return ids.length;
}

// Signing out deletes a person's copies of the forms whose id starts with `formIdPrefix`
// (controllers/form_store_cleanup_controller.js). An autosave still running on one of those forms would write a copy
// straight back: its timer, on the page being signed out of, or the final copy the session poller asks for once the
// sign-out lands, in another tab. So every tab of this browser is told to stop autosaving them first: this page through
// a DOM event, which arrives at once, and the others through a BroadcastChannel. Never throws: the sign-out goes on.
export function suspendAutoSave({ userId, formIdPrefix }) {
  const detail = { userId, formIdPrefix };
  document.dispatchEvent(new CustomEvent(SUSPEND_EVENT, { detail }));
  if (typeof BroadcastChannel !== 'function') return;
  try {
    const channel = new BroadcastChannel(FORM_STORE_CHANNEL);
    channel.postMessage(detail);
    channel.close();
  } catch {
    // No channel in this browser (or it is refused here). This page has stopped; other tabs keep their copies.
  }
}

// Calls `callback({ userId, formIdPrefix })` for each `suspendAutoSave`, from this page or another tab. Returns the
// function that stops listening.
export function onAutoSaveSuspend(callback) {
  const onEvent = (event) => callback(event.detail || {});
  document.addEventListener(SUSPEND_EVENT, onEvent);

  let channel = null;
  if (typeof BroadcastChannel === 'function') {
    try {
      channel = new BroadcastChannel(FORM_STORE_CHANNEL);
      channel.addEventListener('message', (event) => callback(event.data || {}));
    } catch {
      channel = null;
    }
  }

  return () => {
    document.removeEventListener(SUSPEND_EVENT, onEvent);
    channel?.close();
  };
}
